import { createPredicate } from "./condition.js";
import { BrandclubEventType } from "./types.js";
const STANDARD_EVENTS = /* @__PURE__ */ new Set([
  "AddPaymentInfo",
  "AddToCart",
  "AddToWishlist",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "InitiateCheckout",
  "Lead",
  "PageView",
  "Purchase",
  "Schedule",
  "Search",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
  "ViewContent"
]);
let initialized = false;
let debug = false;
const PROVIDER = "Meta";
let emit = true;
let identity = "";
const randomId = "id" + Math.random().toString(36).substring(2, 15);
const warn = (...args) => {
  if (!debug) {
    return;
  }
  console.info(...["[meta-pixel]"].concat(args));
};
const log = (...args) => {
  if (!debug) {
    return;
  }
  console.info(...["[meta-pixel]"].concat(args));
};
const verifyInit = () => {
  if (!initialized) {
    warn("Pixel not initialized before using call ReactPixel.init with required params");
  }
  return initialized;
};
const defaultOptions = {
  autoConfig: true
};
let activeBrandId = "";
const brandPixels = {};
function isPixelInitialized(brandId, pixelId) {
  var _a;
  const pixels = brandPixels == null ? void 0 : brandPixels[brandId];
  if ((_a = pixels == null ? void 0 : pixels.find((item) => item.pixelId === pixelId)) == null ? void 0 : _a.initialized) {
    return true;
  }
  return false;
}
function setPixelInitialized(brandId, pixelId) {
  brandPixels[brandId] = brandPixels[brandId].map((item) => {
    if (item.pixelId === pixelId) {
      return {
        ...item,
        // keep other properties
        pixelId,
        initialized: true,
        eventFilterFn: item.eventFilter ? createPredicate(item.eventFilter) : () => true
      };
    }
    return item;
  });
}
function transformPayloadForMeta(eventType, eventPayload) {
  const transformed = {};
  transformed.currency = eventPayload.currency || "USD";
  const items = eventPayload.checkoutItems || eventPayload.cartItems;
  if (items && Array.isArray(items) && items.length > 0) {
    const contentIds = items.map((item) => item.retailerSku || item.stacklineSku).filter(Boolean);
    if (contentIds.length > 0) {
      transformed.content_ids = contentIds;
    }
    const firstItem = items[0];
    if ((firstItem == null ? void 0 : firstItem.productName) || (firstItem == null ? void 0 : firstItem.title)) {
      transformed.content_name = firstItem.productName || firstItem.title;
    }
    if (firstItem == null ? void 0 : firstItem.categoryName) {
      transformed.content_category = firstItem.categoryName;
    }
    const totalValue = items.reduce((sum, item) => sum + ((item == null ? void 0 : item.retailPrice) || 0), 0);
    if (totalValue > 0) {
      transformed.value = totalValue;
    }
    if (eventType === BrandclubEventType.PurchaseConfirmed || eventType === BrandclubEventType.OrderConfirmation || eventType === BrandclubEventType.OrderSubmitted || eventType === BrandclubEventType.InitiateCheckout) {
      transformed.num_items = items.length;
    }
  } else {
    if (eventPayload.stacklineSku || eventPayload.retailerSku) {
      transformed.content_ids = [eventPayload.retailerSku || eventPayload.stacklineSku];
    }
    if (eventPayload.productName || eventPayload.brandName) {
      transformed.content_name = eventPayload.productName || eventPayload.brandName;
    }
    if (eventPayload.categoryName) {
      transformed.content_category = eventPayload.categoryName;
    }
    if (eventPayload.retailPrice) {
      transformed.value = eventPayload.retailPrice;
    } else if (eventPayload.price) {
      transformed.value = eventPayload.price;
    }
  }
  if (transformed.content_ids && transformed.content_ids.length > 0) {
    transformed.content_type = "product";
  }
  return transformed;
}
const metaPixelHelper = {
  setActiveBrand(brand) {
    activeBrandId = brand;
  },
  unload() {
    const scriptElement = document.getElementById(randomId);
    if (scriptElement) {
      log("unloading fbq script ...", document.removeChild(scriptElement) ? "success" : "failed");
      window.fbq = void 0;
    }
  },
  setBrandPixel(brand, pixelId, eventFilter) {
    if (brand) {
      brandPixels[brand] = brandPixels[brand] || [];
      if (!brandPixels[brand].find((item) => item.pixelId === pixelId) && pixelId) {
        brandPixels[brand].push({
          pixelId,
          eventFilter,
          initialized: false
        });
      } else {
        brandPixels[brand] = brandPixels[brand].map((item) => {
          if (item.pixelId === pixelId) {
            return {
              ...item,
              // keep other properties
              pixelId,
              eventFilter
            };
          }
          return item;
        });
      }
    }
  },
  getActivePixels() {
    return activeBrandId ? brandPixels[activeBrandId] : [];
  },
  initializePixel(brandId, pixelId, options) {
    if (isPixelInitialized(brandId, pixelId)) {
      return;
    }
    metaPixelHelper.init(pixelId, identity ? { external_id: identity } : {}, options || defaultOptions);
    setPixelInitialized(brandId, pixelId);
  },
  init(pixelId, advancedMatching = {}, options = defaultOptions) {
    initialized = typeof window !== "undefined" && !!window.fbq;
    if (!initialized) {
      initialized = true;
      log("initializing fbq script");
      (function(f, b, e, v, n, t, s) {
        var _a;
        if (f.fbq) return;
        n = f.fbq = function(...args) {
          n.callMethod ? n.callMethod(...args) : n.queue.push(args);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.id = randomId;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        (_a = s == null ? void 0 : s.parentNode) == null ? void 0 : _a.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    }
    if (!pixelId) {
      warn("Please insert pixel id for initializing");
    } else {
      if ((options == null ? void 0 : options.autoConfig) === false) {
        window.fbq("set", "autoConfig", false, pixelId);
        log(`called fbq('set', 'autoConfig', false, '${pixelId}');`);
      }
      window.fbq("init", pixelId, advancedMatching);
      log(`called fbq('init', '${pixelId}');`);
    }
  },
  trackSingle(title, data, isCustom = false, eventId) {
    if (!verifyInit()) {
      return;
    }
    const activePixels = metaPixelHelper.getActivePixels();
    const eventType = isCustom ? "trackSingleCustom" : "trackSingle";
    if (activePixels && (activePixels == null ? void 0 : activePixels.length) > 0) {
      activePixels.forEach((pixel) => {
        if (emit) {
          if (pixel.eventFilterFn && !pixel.eventFilterFn(data)) {
            log("eventFilterFn failed", pixel.eventFilterFn, data);
            return;
          }
          const options = eventId ? { eventID: eventId } : void 0;
          window.fbq(eventType, pixel.pixelId, title, data, options);
        }
        if (debug) {
          log(`called fbq('${eventType}', '${pixel.pixelId}', '${title}'${eventId ? `, {eventID: '${eventId}'}` : ""}); with data `, data);
        }
      });
    }
  },
  trackSingleCustom(eventName, data) {
    return metaPixelHelper.trackSingle(eventName, data, true);
  }
};
function setDataProcessingOptions(lduOptions) {
  if (!verifyInit()) {
    return;
  }
  if (lduOptions.enabled) {
    window.fbq("dataProcessingOptions", ["LDU"], lduOptions.country, lduOptions.state);
    log(`LDU enabled: dataProcessingOptions(['LDU'], ${lduOptions.country}, ${lduOptions.state})`);
  } else {
    window.fbq("dataProcessingOptions", [], 0, 0);
    log("LDU disabled: dataProcessingOptions([], 0, 0)");
  }
}
const metaPixel = {
  enableDebug() {
    debug = true;
  },
  setIdentity(id) {
    identity = id;
  },
  enableEmitMetrics(v = true) {
    emit = v;
  },
  /**
   * Set LDU data processing options (exposed for external use if needed)
   */
  setDataProcessingOptions,
  track({
    brandId,
    pixels,
    eventName,
    eventType,
    eventPayload,
    trackingOptions,
    externalTracking
  }) {
    var _a;
    pixels = pixels == null ? void 0 : pixels.filter((item) => item.pixelProvider === PROVIDER);
    metaPixelHelper.setActiveBrand(brandId);
    if (Array.isArray(pixels) && (pixels == null ? void 0 : pixels.length) > 0) {
      if (pixels.length > 0) {
        pixels.forEach((pixel) => {
          metaPixelHelper.setBrandPixel(brandId, pixel.pixelId, pixel.eventFilter);
          metaPixelHelper.initializePixel(brandId, pixel.pixelId, pixel.pixelOptions);
        });
      }
      const isPurchaseEvent = eventType === BrandclubEventType.PurchaseConfirmed || eventType === BrandclubEventType.OrderSubmitted || eventType === BrandclubEventType.OrderConfirmation;
      if (isPurchaseEvent && ((_a = trackingOptions == null ? void 0 : trackingOptions.ldu) == null ? void 0 : _a.enabled)) {
        setDataProcessingOptions(trackingOptions.ldu);
      }
      if (eventType === BrandclubEventType.AddToCart || eventType === BrandclubEventType.PurchaseConfirmed || eventType === BrandclubEventType.OrderSubmitted || eventType === BrandclubEventType.ViewCart || eventType === BrandclubEventType.InitiateCheckout || eventType === BrandclubEventType.OrderConfirmation) {
        eventPayload = transformPayloadForMeta(eventType, eventPayload);
      }
      const isCustomEvent = !STANDARD_EVENTS.has(eventName);
      const metaReport = externalTracking == null ? void 0 : externalTracking.find((r) => r.provider === "Meta");
      const eventId = isPurchaseEvent ? metaReport == null ? void 0 : metaReport.eventId : void 0;
      metaPixelHelper.trackSingle(eventName, eventPayload, isCustomEvent, eventId);
    } else {
      metaPixelHelper.unload();
    }
  },
  grantConsent() {
    if (!verifyInit()) {
      return;
    }
    window.fbq("consent", "grant");
    log(`Meta Pixel consent granted`);
  },
  revokeConsent() {
    if (!verifyInit()) {
      return;
    }
    window.fbq("consent", "revoke");
    log(`Meta Pixel consent revoked`);
  }
};
export {
  metaPixel as default
};
//# sourceMappingURL=metaPixel.js.map
